// Google Classroom roster import (client-side, no backend).
//
// Uses Google Identity Services (GIS) for INCREMENTAL authorization: we do NOT ask
// for Classroom scopes at login (that would scare off every teacher). Instead, only
// when a teacher clicks "Import from Google Classroom" do we request a short-lived
// access token for the read-only Classroom scopes, then call the Classroom REST API
// straight from the browser (2 calls: list the teacher's courses, list a course's
// students). Names + school emails flow into the existing saveStudents importer.
//
// SETUP (one-time, by an admin — see IDEAS.md "Google Classroom import"):
//   1. Google Cloud console → enable the "Google Classroom API" on project `classboon`.
//   2. OAuth consent screen → add the three scopes below → add test users (or submit
//      for verification so any teacher can use it — SENSITIVE scopes, no paid audit).
//   3. Set REACT_APP_GOOGLE_CLIENT_ID to the project's OAuth 2.0 **Web** client ID
//      (the same one Firebase Google sign-in uses is fine; its authorized origins
//      already include the app domains). Until it's set, the import UI stays hidden.

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// Read-only: the teacher's courses, their rosters, and student email addresses.
const SCOPES = [
  "https://www.googleapis.com/auth/classroom.courses.readonly",
  "https://www.googleapis.com/auth/classroom.rosters.readonly",
  "https://www.googleapis.com/auth/classroom.profile.emails",
].join(" ");

const API = "https://classroom.googleapis.com/v1";

// True once the OAuth client ID is configured — the import tab is hidden until then.
export function isClassroomConfigured() {
  return !!CLIENT_ID;
}

// Load the GIS script once, lazily (only when the teacher actually imports).
let gisPromise = null;
function loadGis() {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Couldn't load Google sign-in. Check your connection and try again."));
    document.head.appendChild(s);
  });
  return gisPromise;
}

// Request an access token for the Classroom scopes (opens the Google consent popup
// the first time; silent afterwards while the grant lasts).
export async function getClassroomToken() {
  if (!CLIENT_ID) throw new Error("Google Classroom import isn't set up yet.");
  await loadGis();
  return new Promise((resolve, reject) => {
    try {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPES,
        callback: (resp) => {
          if (resp && resp.access_token) resolve(resp.access_token);
          else reject(new Error("Google didn't grant access to Classroom."));
        },
        error_callback: (err) => reject(new Error(err?.message || "Google sign-in was cancelled.")),
      });
      client.requestAccessToken();
    } catch (e) {
      reject(e);
    }
  });
}

async function apiGet(path, token) {
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    let detail = "";
    try { detail = (await res.json())?.error?.message || ""; } catch { /* ignore */ }
    if (res.status === 403) {
      throw new Error(detail || "Google Classroom access was denied (the API may not be enabled, or your account isn't allowed yet).");
    }
    throw new Error(detail || `Google Classroom error (${res.status}).`);
  }
  return res.json();
}

// The teacher's ACTIVE courses (paginated).
export async function listCourses(token) {
  const out = [];
  let pageToken = "";
  do {
    const q = new URLSearchParams({ teacherId: "me", courseStates: "ACTIVE", pageSize: "100" });
    if (pageToken) q.set("pageToken", pageToken);
    const data = await apiGet(`/courses?${q}`, token);
    (data.courses || []).forEach((c) => out.push({ id: c.id, name: c.name, section: c.section || "" }));
    pageToken = data.nextPageToken || "";
  } while (pageToken);
  return out;
}

// Students in one course → { firstName, lastName, email } rows for saveStudents.
export async function listCourseStudents(token, courseId) {
  const out = [];
  let pageToken = "";
  do {
    const q = new URLSearchParams({ pageSize: "100" });
    if (pageToken) q.set("pageToken", pageToken);
    const data = await apiGet(`/courses/${courseId}/students?${q}`, token);
    (data.students || []).forEach((s) => {
      const p = s.profile || {};
      const n = p.name || {};
      const full = n.fullName || "";
      out.push({
        firstName: n.givenName || full.split(/\s+/)[0] || "",
        lastName: n.familyName || full.split(/\s+/).slice(1).join(" ") || "",
        email: p.emailAddress || "",
      });
    });
    pageToken = data.nextPageToken || "";
  } while (pageToken);
  return out;
}
